"use client";

import { usePathname } from "@/i18n/navigation";
import {
  generateBreadcrumbs,
  generateBreadcrumbsFromPath,
} from "@/utils/breadcrumbGenerator";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import Breadcrumb from "./BreadCrumb";

// Translation keys for static texts
// Key: breadcrumb.brandName - Text: "Turna"
// Key: breadcrumb.flightTicket - Text: "Uçak Bileti"
// Key: breadcrumb.defaultPage - Text: "Sayfa"

// capitalizeName fonksiyonunu burada tanımlayalım (utility'den import edemiyoruz)
function capitalizeFirstLetter(str) {
  if (!str || typeof str !== "string") return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function capitalizeName(str) {
  if (!str || typeof str !== "string") return str;
  return str
    .split(/[\s\-\.]+/)
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
}

/**
 * Dinamik breadcrumb component'i
 * URL'den otomatik olarak breadcrumb oluşturur
 */
export default function DynamicBreadcrumb({
  pageType = null,
  params = {},
  pageData = {},
  customItems = null,
  className = "",
}) {
  const pathname = usePathname();
  const t = useTranslationDetector("breadcrumb");

  // Özel items verilmişse onları kullan
  if (customItems) {
    return <Breadcrumb items={customItems} className={className} />;
  }

  // Sayfa türü belirtilmişse template kullan
  if (pageType) {
    const items = generateBreadcrumbs(pageType, params, t);
    return <Breadcrumb items={items} className={className} />;
  }

  // URL'den otomatik oluştur
  const items = generateBreadcrumbsFromPath(pathname, pageData, t);
  return <Breadcrumb items={items} className={className} />;
}

/**
 * Landing page'ler için özel breadcrumb component'i
 */
export function LandingPageBreadcrumb({
  pageData,
  landingPageName,
  className = "",
}) {
  const t = useTranslationDetector("breadcrumb");

  // Önce landingPageName'den route olup olmadığını kontrol et (ankara-izmir formatı)
  if (landingPageName && landingPageName.includes("-")) {
    const parts = landingPageName.split("-");
    if (parts.length >= 2) {
      const origin = parts[0];
      const destination = parts[1];
      const items = [
        { label: t("brandName"), href: "/" },
        { label: t("flightTicket"), href: "/flight" },
        {
          label: `${capitalizeName(origin)} - ${capitalizeName(destination)}`,
          href: `/flight/${landingPageName}`,
        },
      ];
      return <Breadcrumb items={items} className={className} />;
    }
  }

  // pageData yoksa veya Type yoksa, normal landing page breadcrumb oluştur
  if (!pageData || !pageData.Type) {
    const items = [
      { label: t("brandName"), href: "/" },
      { label: t("flightTicket"), href: "/flight" },
      {
        label: landingPageName
          ? capitalizeName(landingPageName)
          : t("defaultPage"),
        href: `/flight/${landingPageName}`,
      },
    ];
    return <Breadcrumb items={items} className={className} />;
  }

  let pageType = null;
  let params = {};

  // Sayfa türüne göre breadcrumb oluştur
  switch (pageData.Type) {
    case "city":
      pageType = "cityLanding";
      params = {
        cityName: pageData.City || landingPageName,
        cityCode: pageData.CityCode,
      };
      break;

    case "airline":
      pageType = "airlineLanding";
      params = {
        airlineName: pageData.Airline || landingPageName,
      };
      break;

    case "route":
      pageType = "routeLanding";
      // pageData'dan origin ve destination'ı al, yoksa landingPageName'den parse et
      let origin = pageData.Origin;
      let destination = pageData.Destination;

      // Eğer pageData'da yoksa, landingPageName'den parse et (ankara-izmir formatı)
      if (!origin || !destination) {
        const parts = landingPageName?.split("-");
        if (parts && parts.length >= 2) {
          origin = origin || parts[0];
          destination = destination || parts[1];
        }
      }

      params = {
        origin,
        destination,
      };
      break;

    case "airport":
      pageType = "airportLanding";
      params = {
        airportName: pageData.AirportName || landingPageName,
        airportCode: pageData.AirportCode,
      };
      break;

    default:
      // Varsayılan breadcrumb - capitalize kullan
      const items = [
        { label: t("brandName"), href: "/" },
        { label: t("flightTicket"), href: "/flight" },
        {
          label: landingPageName
            ? capitalizeName(landingPageName)
            : t("defaultPage"),
          href: `/flight/${landingPageName}`,
        },
      ];
      return <Breadcrumb items={items} className={className} />;
  }

  return (
    <DynamicBreadcrumb
      pageType={pageType}
      params={params}
      className={className}
    />
  );
}
